import type { ConfigItem } from '../types';

/**
 * Template-Specific Default Configuration Items
 * 
 * This file contains default configuration items specific to different consulting templates.
 * Each template has its own methodology and deliverables structure.
 */

// ===== ATLASSIAN TEMPLATE =====
// Based on Atlassian ITSM SOW methodology with 6 phases

export const ATLASSIAN_CONFIG_ITEMS: Omit<ConfigItem, 'id'>[] = [
  // ===== PHASE 1: INITIATION =====
  {
    name: 'Project Kickoff Meeting',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Initial project kickoff with all stakeholders to align on goals, timeline, and success criteria',
    coreFeature: true
  },
  {
    name: 'Governance Framework Setup',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Define project governance structure, decision-making process, and escalation paths',
    coreFeature: true
  },
  {
    name: 'Communication Plan',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Establish communication cadence, channels, and stakeholder reporting structure',
    coreFeature: true
  },
  {
    name: 'Environment Readiness Assessment',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Verify Atlassian instance access, licensing, and technical prerequisites',
    coreFeature: true
  },
  {
    name: 'Stakeholder Alignment',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Map key stakeholders, conduct initial interviews, and document business objectives',
    coreFeature: true
  },

  // ===== PHASE 2: DISCOVERY (ITIL 4 WORKSHOPS) =====
  {
    name: 'Incident Management Workshop',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to define incident management processes, priorities, and workflows',
    coreFeature: true
  },
  {
    name: 'Request Management Workshop',
    category: 'request-type-group',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to catalog service requests and define fulfillment processes',
    coreFeature: true
  },
  {
    name: 'Problem Management Workshop',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to establish root cause analysis and problem resolution processes',
    coreFeature: true
  },
  {
    name: 'Change Management Workshop',
    category: 'approval',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to define change types, CAB process, and approval workflows',
    coreFeature: true
  },
  {
    name: 'Knowledge Management Workshop',
    category: 'knowledge-base',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to plan knowledge article structure, lifecycle, and governance',
    coreFeature: true
  },
  {
    name: 'SLA Definition Workshop',
    category: 'sla',
    defaultPhase: 2,
    description: 'Define service level agreements, targets, and measurement criteria for all service types',
    coreFeature: true
  },
  {
    name: 'Assets (CMDB) Requirements',
    category: 'asset',
    defaultPhase: 2,
    description: 'Define asset types, relationships, and CMDB schema requirements',
    requiresAssets: true,
    defaultAssigneeRole: 'consultant'
  },
  {
    name: 'Opsgenie Integration Planning',
    category: 'integration',
    defaultPhase: 2,
    description: 'Define on-call schedules, escalation policies, and alerting requirements for Opsgenie',
    coreFeature: false
  },
  {
    name: 'Statuspage Configuration Planning',
    category: 'integration',
    defaultPhase: 2,
    description: 'Plan public/private status page structure, components, and incident communication',
    coreFeature: false
  },
  {
    name: 'Guard (SSO/SCIM) Requirements',
    category: 'integration',
    defaultPhase: 2,
    description: 'Define single sign-on requirements, user provisioning needs, and compliance requirements',
    coreFeature: false
  },
  {
    name: 'Automation & Integration Discovery',
    category: 'automation',
    defaultPhase: 2,
    description: 'Identify automation opportunities and third-party integration requirements',
    coreFeature: false
  },

  // ===== PHASE 3: PLANNING =====
  {
    name: 'Solution Design Documentation',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Create comprehensive solution design document covering all discovery findings',
    coreFeature: true
  },
  {
    name: 'Sprint Roadmap Creation',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Define implementation sprints, deliverables, and milestone dates',
    coreFeature: true
  },
  {
    name: 'Test Plan Development',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Create detailed test plan covering functional, UAT, and integration testing',
    coreFeature: true
  },
  {
    name: 'Migration Strategy Planning',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Plan data migration approach, cutover strategy, and rollback procedures',
    coreFeature: false
  },
  {
    name: 'Resource Allocation',
    category: 'team-settings',
    defaultPhase: 3,
    description: 'Assign implementation tasks to team members and validate capacity',
    coreFeature: true
  },

  // ===== PHASE 4: IMPLEMENTATION =====
  {
    name: 'JSM Project(s) Configuration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Create and configure JSM project(s) with proper permissions and settings',
    coreFeature: true
  },
  {
    name: 'Workflow Design & Implementation',
    category: 'automation',
    defaultPhase: 4,
    description: 'Build workflows for incidents, requests, problems, and changes',
    coreFeature: true
  },
  {
    name: 'Request Type Configuration',
    category: 'request-type-group',
    defaultPhase: 4,
    description: 'Configure all request types with fields, icons, and portal categories',
    coreFeature: true
  },
  {
    name: 'SLA Policy Implementation',
    category: 'sla',
    defaultPhase: 4,
    description: 'Configure SLA goals, calendars, and breach notifications',
    coreFeature: true
  },
  {
    name: 'Assets Schema Design',
    category: 'asset',
    defaultPhase: 4,
    description: 'Build Assets object schemas with attributes and relationships',
    requiresAssets: true,
    defaultAssigneeRole: 'consultant'
  },
  {
    name: 'Opsgenie Policy Configuration',
    category: 'integration',
    defaultPhase: 4,
    description: 'Configure on-call teams, escalation chains, and routing rules in Opsgenie',
    coreFeature: false
  },
  {
    name: 'Guard SSO/SCIM Setup',
    category: 'integration',
    defaultPhase: 4,
    description: 'Configure Atlassian Guard for SSO/SCIM integration with identity provider',
    coreFeature: false
  },
  {
    name: 'Confluence Knowledge Base Integration',
    category: 'knowledge-base',
    defaultPhase: 4,
    description: 'Link Confluence spaces and configure knowledge article integration',
    coreFeature: false
  },
  {
    name: 'Automation Rule Creation',
    category: 'automation',
    defaultPhase: 4,
    description: 'Build automation rules for assignment, notifications, and workflow transitions',
    coreFeature: true
  },
  {
    name: 'Third-Party Integrations',
    category: 'integration',
    defaultPhase: 4,
    description: 'Configure integrations with monitoring tools, CMDB, or other external systems',
    coreFeature: false
  },
  {
    name: 'Queue Configuration',
    category: 'queue',
    defaultPhase: 4,
    description: 'Create agent queues with JQL filters for work organization',
    coreFeature: true
  },
  {
    name: 'Portal Branding & Customization',
    category: 'portal',
    defaultPhase: 4,
    description: 'Apply corporate branding, logos, and custom CSS to customer portal',
    coreFeature: false
  },
  {
    name: 'Email Channel Setup',
    category: 'email',
    defaultPhase: 4,
    description: 'Configure email request channels and automated responses',
    coreFeature: false
  },
  {
    name: 'Notification Configuration',
    category: 'notification',
    defaultPhase: 4,
    description: 'Set up email and in-app notifications for agents and customers',
    coreFeature: true
  },

  // ===== PHASE 5: TESTING =====
  {
    name: 'Functional Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Validate all workflows, automations, and configurations work as designed',
    coreFeature: true
  },
  {
    name: 'User Acceptance Testing (UAT)',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Conduct UAT sessions with key users to validate business requirements',
    coreFeature: true
  },
  {
    name: 'Access Verification',
    category: 'team-settings',
    defaultPhase: 5,
    description: 'Verify permissions, roles, and access controls for all user groups',
    coreFeature: true
  },
  {
    name: 'SSO/SCIM Validation',
    category: 'integration',
    defaultPhase: 5,
    description: 'Test single sign-on authentication and automated user provisioning',
    coreFeature: false
  },
  {
    name: 'Integration Testing',
    category: 'integration',
    defaultPhase: 5,
    description: 'Validate all third-party integrations and data flows',
    coreFeature: false
  },
  {
    name: 'Performance Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Test system performance under expected load conditions',
    coreFeature: false
  },
  {
    name: 'SLA Accuracy Testing',
    category: 'sla',
    defaultPhase: 5,
    description: 'Verify SLA calculations, breach notifications, and calendar configurations',
    coreFeature: true
  },

  // ===== PHASE 6: LAUNCH/SUPPORT =====
  {
    name: 'End-User Training',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Conduct training sessions for end users on portal usage and request submission',
    coreFeature: true
  },
  {
    name: 'Admin Training',
    category: 'team-settings',
    defaultPhase: 6,
    description: 'Train administrators on configuration management and ongoing maintenance',
    coreFeature: true
  },
  {
    name: 'Agent Training',
    category: 'team-settings',
    defaultPhase: 6,
    description: 'Train service desk agents on queue management, ticket handling, and workflows',
    coreFeature: true
  },
  {
    name: 'Hypercare Support',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Provide intensive post-launch support during critical stabilization period',
    coreFeature: true
  },
  {
    name: 'Adoption Enablement',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Monitor adoption metrics and provide targeted support to drive usage',
    coreFeature: false
  },
  {
    name: 'Post-Launch Review',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Conduct retrospective and document lessons learned',
    coreFeature: true
  },
  {
    name: 'Knowledge Transfer',
    category: 'knowledge-base',
    defaultPhase: 6,
    description: 'Complete documentation handoff and knowledge transfer to support team',
    coreFeature: true
  },
  {
    name: 'Performance Dashboard Setup',
    category: 'dashboard',
    defaultPhase: 6,
    description: 'Configure KPI dashboards for ongoing monitoring and reporting',
    coreFeature: false
  }
];

// ===== SERVICENOW TEMPLATE =====
// Based on ServiceNow ITSM implementation methodology with ITIL 4 best practices

export const SERVICENOW_CONFIG_ITEMS: Omit<ConfigItem, 'id'>[] = [
  // ===== PHASE 1: INITIATION & FOUNDATION =====
  {
    name: 'Project Kickoff & Stakeholder Alignment',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Initial project kickoff with stakeholders to define scope, objectives, timeline, and success criteria',
    coreFeature: true
  },
  {
    name: 'Governance & RACI Framework',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Establish project governance, decision-making authority, and RACI matrix for implementation',
    coreFeature: true
  },
  {
    name: 'Instance Provisioning & Setup',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Provision ServiceNow instance (Dev, Test, Prod), configure basic properties and system settings',
    coreFeature: true
  },
  {
    name: 'Environment Strategy',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Define update set strategy, deployment pipeline, and environment promotion procedures',
    coreFeature: true
  },
  {
    name: 'User & Group Architecture Planning',
    category: 'team-settings',
    defaultPhase: 1,
    description: 'Plan organizational structure, groups, roles, and user provisioning approach',
    coreFeature: true
  },
  {
    name: 'Communication & Reporting Plan',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Establish communication cadence, status reporting, and stakeholder engagement plan',
    coreFeature: true
  },

  // ===== PHASE 2: DISCOVERY & REQUIREMENTS =====
  {
    name: 'Incident Management Workshop',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to define incident categorization, priority matrix, assignment rules, and escalation',
    coreFeature: true
  },
  {
    name: 'Request Management Workshop',
    category: 'request-type-group',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to catalog service requests, define fulfillment workflows, and SLAs',
    coreFeature: true
  },
  {
    name: 'Problem Management Workshop',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to establish root cause analysis process, known error database, and problem workflows',
    coreFeature: true
  },
  {
    name: 'Change Management Workshop',
    category: 'approval',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to define change types (standard, normal, emergency), CAB process, and approvals',
    coreFeature: true
  },
  {
    name: 'Knowledge Management Workshop',
    category: 'knowledge-base',
    defaultPhase: 2,
    description: 'ITIL 4 workshop to plan knowledge base structure, article lifecycle, and governance',
    coreFeature: true
  },
  {
    name: 'CMDB Requirements & Design',
    category: 'asset',
    defaultPhase: 2,
    description: 'Define CI classes, relationships, attributes, and CMDB population strategy',
    requiresAssets: true,
    defaultAssigneeRole: 'consultant'
  },
  {
    name: 'Service Catalog Discovery',
    category: 'request-type-group',
    defaultPhase: 2,
    description: 'Identify catalog items, request types, and service offerings to be deployed',
    coreFeature: true
  },
  {
    name: 'SLA & OLA Definition',
    category: 'sla',
    defaultPhase: 2,
    description: 'Define service level agreements, operational level agreements, and measurement criteria',
    coreFeature: true
  },
  {
    name: 'Integration Requirements Gathering',
    category: 'integration',
    defaultPhase: 2,
    description: 'Identify integration points with LDAP, SSO, monitoring tools, CMDB sources, and third-party systems',
    coreFeature: false
  },
  {
    name: 'Data Migration Assessment',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Assess legacy data migration needs, data quality, and migration approach',
    coreFeature: false
  },
  {
    name: 'Automation & Orchestration Discovery',
    category: 'automation',
    defaultPhase: 2,
    description: 'Identify automation opportunities for workflows, notifications, and orchestration tasks',
    coreFeature: false
  },
  {
    name: 'Security & Compliance Review',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Review security requirements, compliance needs (SOC2, HIPAA, etc.), and access control policies',
    coreFeature: false
  },
  {
    name: 'Reporting & Analytics Requirements',
    category: 'dashboard',
    defaultPhase: 2,
    description: 'Define KPIs, dashboards, and reporting requirements for stakeholders',
    coreFeature: false
  },

  // ===== PHASE 3: DESIGN & PLANNING =====
  {
    name: 'Solution Design Document',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Create comprehensive solution architecture document covering all modules and integrations',
    coreFeature: true
  },
  {
    name: 'Technical Design Specification',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Document technical design including business rules, scripts, workflows, and customizations',
    coreFeature: true
  },
  {
    name: 'Data Model Design',
    category: 'asset',
    defaultPhase: 3,
    description: 'Design custom tables, fields, and relationships required for the implementation',
    coreFeature: true
  },
  {
    name: 'Integration Architecture',
    category: 'integration',
    defaultPhase: 3,
    description: 'Design integration architecture including REST APIs, MID Server requirements, and data flows',
    coreFeature: false
  },
  {
    name: 'Sprint Planning & Roadmap',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Define implementation sprints, deliverables, and milestone timeline',
    coreFeature: true
  },
  {
    name: 'Test Strategy & Plan',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Develop comprehensive test plan covering unit, integration, UAT, and performance testing',
    coreFeature: true
  },
  {
    name: 'Training & Adoption Plan',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Plan training approach, materials, and user adoption strategy',
    coreFeature: true
  },
  {
    name: 'Cutover & Deployment Plan',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Define go-live strategy, cutover activities, rollback procedures, and hypercare plan',
    coreFeature: true
  },

  // ===== PHASE 4: BUILD & CONFIGURATION =====
  {
    name: 'User & Group Configuration',
    category: 'team-settings',
    defaultPhase: 4,
    description: 'Create groups, roles, and configure user access controls and permissions',
    coreFeature: true
  },
  {
    name: 'Incident Management Configuration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Configure incident table, states, priorities, categories, and assignment rules',
    coreFeature: true
  },
  {
    name: 'Problem Management Configuration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Configure problem table, known error database, and problem workflows',
    coreFeature: true
  },
  {
    name: 'Change Management Configuration',
    category: 'approval',
    defaultPhase: 4,
    description: 'Configure change request types, states, CAB workflow, and approval processes',
    coreFeature: true
  },
  {
    name: 'Service Catalog Build',
    category: 'request-type-group',
    defaultPhase: 4,
    description: 'Build catalog categories, items, variables, and request fulfillment workflows',
    coreFeature: true
  },
  {
    name: 'Workflow & Flow Designer Configuration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Build workflows and flows for request fulfillment, approvals, and process automation',
    coreFeature: true
  },
  {
    name: 'SLA Configuration',
    category: 'sla',
    defaultPhase: 4,
    description: 'Configure SLA definitions, schedules, conditions, and escalation workflows',
    coreFeature: true
  },
  {
    name: 'CMDB Population & Configuration',
    category: 'asset',
    defaultPhase: 4,
    description: 'Configure CI classes, relationships, and populate CMDB with discovery or imports',
    requiresAssets: true,
    defaultAssigneeRole: 'consultant'
  },
  {
    name: 'Discovery Tool Configuration',
    category: 'integration',
    defaultPhase: 4,
    description: 'Configure ServiceNow Discovery for automated CMDB population (if applicable)',
    coreFeature: false
  },
  {
    name: 'Knowledge Base Setup',
    category: 'knowledge-base',
    defaultPhase: 4,
    description: 'Configure knowledge base structure, categories, and article templates',
    coreFeature: true
  },
  {
    name: 'Business Rules & Scripting',
    category: 'automation',
    defaultPhase: 4,
    description: 'Develop business rules, client scripts, and UI policies for automation and validation',
    coreFeature: true
  },
  {
    name: 'Notification Configuration',
    category: 'notification',
    defaultPhase: 4,
    description: 'Configure email notifications, events, and notification templates',
    coreFeature: true
  },
  {
    name: 'Service Portal Configuration',
    category: 'portal',
    defaultPhase: 4,
    description: 'Configure Service Portal, branding, widgets, and user experience',
    coreFeature: true
  },
  {
    name: 'SSO & Authentication Setup',
    category: 'integration',
    defaultPhase: 4,
    description: 'Configure Single Sign-On with SAML, OAuth, or LDAP integration',
    coreFeature: false
  },
  {
    name: 'LDAP/Active Directory Integration',
    category: 'integration',
    defaultPhase: 4,
    description: 'Configure LDAP integration for user import and authentication',
    coreFeature: false
  },
  {
    name: 'Email Integration',
    category: 'integration',
    defaultPhase: 4,
    description: 'Configure inbound email processing and email channels for ticket creation',
    coreFeature: false
  },
  {
    name: 'Third-Party Tool Integrations',
    category: 'integration',
    defaultPhase: 4,
    description: 'Configure REST/SOAP integrations with monitoring, ITSM, or other enterprise tools',
    coreFeature: false
  },
  {
    name: 'MID Server Setup',
    category: 'integration',
    defaultPhase: 4,
    description: 'Install and configure MID Server for on-premise integrations and discovery',
    coreFeature: false
  },
  {
    name: 'Reporting & Dashboard Creation',
    category: 'dashboard',
    defaultPhase: 4,
    description: 'Build reports, dashboards, and KPI scorecards for operational monitoring',
    coreFeature: true
  },
  {
    name: 'Performance Analytics Configuration',
    category: 'dashboard',
    defaultPhase: 4,
    description: 'Configure Performance Analytics for trending, benchmarking, and predictive intelligence',
    coreFeature: false
  },
  {
    name: 'Mobile App Configuration',
    category: 'portal',
    defaultPhase: 4,
    description: 'Configure ServiceNow mobile app experience and mobile-specific features',
    coreFeature: false
  },
  {
    name: 'Virtual Agent (Chatbot) Setup',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure Virtual Agent for automated request handling and self-service',
    coreFeature: false
  },
  {
    name: 'Assignment Rules & Work Allocation',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure assignment groups, rules, and work distribution logic',
    coreFeature: true
  },
  {
    name: 'UI Customization & Branding',
    category: 'portal',
    defaultPhase: 4,
    description: 'Apply corporate branding, custom themes, and UI customizations',
    coreFeature: false
  },
  {
    name: 'Data Migration Execution',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Execute data migration from legacy systems using import sets and transform maps',
    coreFeature: false
  },

  // ===== PHASE 5: TESTING & VALIDATION =====
  {
    name: 'Unit & Integration Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Conduct unit testing of configurations and integration testing across modules',
    coreFeature: true
  },
  {
    name: 'User Acceptance Testing (UAT)',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Facilitate UAT sessions with business users to validate requirements',
    coreFeature: true
  },
  {
    name: 'Workflow & Automation Testing',
    category: 'automation',
    defaultPhase: 5,
    description: 'Validate all workflows, flows, business rules, and automation scripts',
    coreFeature: true
  },
  {
    name: 'Integration Testing',
    category: 'integration',
    defaultPhase: 5,
    description: 'Test all third-party integrations, SSO, LDAP, and data flows',
    coreFeature: false
  },
  {
    name: 'SLA & Performance Testing',
    category: 'sla',
    defaultPhase: 5,
    description: 'Validate SLA calculations, escalations, and system performance under load',
    coreFeature: true
  },
  {
    name: 'Security & Access Control Testing',
    category: 'team-settings',
    defaultPhase: 5,
    description: 'Verify role-based access controls, ACLs, and security configurations',
    coreFeature: true
  },
  {
    name: 'Regression Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Conduct regression testing after fixes and configuration changes',
    coreFeature: true
  },
  {
    name: 'Performance & Load Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Test system performance, response times, and scalability under expected load',
    coreFeature: false
  },

  // ===== PHASE 6: DEPLOYMENT & SUPPORT =====
  {
    name: 'End User Training',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Deliver training for end users on Service Portal, catalog, and request submission',
    coreFeature: true
  },
  {
    name: 'Admin & Power User Training',
    category: 'team-settings',
    defaultPhase: 6,
    description: 'Train administrators and power users on platform configuration and maintenance',
    coreFeature: true
  },
  {
    name: 'Fulfiller & Agent Training',
    category: 'team-settings',
    defaultPhase: 6,
    description: 'Train service desk agents and fulfillers on ticket management and workflows',
    coreFeature: true
  },
  {
    name: 'Production Deployment',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Execute production deployment including update set migration and cutover',
    coreFeature: true
  },
  {
    name: 'Go-Live Support & Hypercare',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Provide intensive post-launch support during critical stabilization period',
    coreFeature: true
  },
  {
    name: 'Performance Monitoring & Optimization',
    category: 'dashboard',
    defaultPhase: 6,
    description: 'Monitor system performance, optimize slow queries, and tune configurations',
    coreFeature: false
  },
  {
    name: 'Knowledge Transfer & Documentation',
    category: 'knowledge-base',
    defaultPhase: 6,
    description: 'Complete documentation handoff including technical docs, runbooks, and SOPs',
    coreFeature: true
  },
  {
    name: 'Adoption Enablement & Analytics',
    category: 'dashboard',
    defaultPhase: 6,
    description: 'Monitor adoption metrics, identify improvement areas, and drive user engagement',
    coreFeature: false
  },
  {
    name: 'Post-Implementation Review',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Conduct lessons learned session and document project successes and improvements',
    coreFeature: true
  },
  {
    name: 'Continuous Improvement Planning',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Define roadmap for Phase 2 enhancements and ongoing optimization',
    coreFeature: false
  }
];

// ===== IVANTI TEMPLATE =====
// Ivanti Neurons for ITSM (formerly Ivanti Service Manager) implementation methodology
// Based on Ivanti best practices with 5-phase approach

export const IVANTI_CONFIG_ITEMS: Omit<ConfigItem, 'id'>[] = [
  // ===== PHASE 1: FOUNDATION & PLANNING =====
  {
    name: 'Project Kickoff & Charter',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Define project scope, objectives, stakeholders, and success criteria for Ivanti implementation',
    coreFeature: true
  },
  {
    name: 'Technical Environment Setup',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Configure Ivanti Neurons platform, establish environments (Dev, Test, Prod), and verify system access',
    coreFeature: true
  },
  {
    name: 'Governance Framework',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Establish project governance, decision-making process, and change control procedures',
    coreFeature: true
  },
  {
    name: 'Integration Architecture Planning',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Design integration architecture for Active Directory, monitoring tools, and third-party systems',
    coreFeature: true
  },
  {
    name: 'Data Migration Strategy',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Plan approach for migrating historical data, tickets, assets, and knowledge articles',
    coreFeature: false
  },
  {
    name: 'Security & Role Planning',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Define security model, roles, teams, and access control structure',
    coreFeature: true
  },

  // ===== PHASE 2: DISCOVERY & REQUIREMENTS =====
  {
    name: 'Business Process Discovery',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Document current-state processes for Incident, Request, Change, and Problem Management',
    coreFeature: true
  },
  {
    name: 'Incident Management Requirements',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Define incident workflow, categorization, priorities, SLA requirements, and escalation rules',
    coreFeature: true
  },
  {
    name: 'Service Request Requirements',
    category: 'request-type-group',
    defaultPhase: 2,
    description: 'Catalog service requests, define fulfillment workflows, and approval requirements',
    coreFeature: true
  },
  {
    name: 'Change Management Requirements',
    category: 'approval',
    defaultPhase: 2,
    description: 'Define change types, CAB process, risk assessment, and approval workflows',
    coreFeature: true
  },
  {
    name: 'Problem Management Requirements',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Define problem workflow, root cause analysis process, and known error database structure',
    coreFeature: true
  },
  {
    name: 'Asset & CMDB Requirements',
    category: 'asset',
    defaultPhase: 2,
    description: 'Define CI types, relationships, attributes, and discovery integration requirements',
    requiresAssets: true
  },
  {
    name: 'Knowledge Management Requirements',
    category: 'knowledge-base',
    defaultPhase: 2,
    description: 'Plan knowledge base structure, article templates, lifecycle, and search capabilities',
    coreFeature: true
  },
  {
    name: 'SLA & Metric Requirements',
    category: 'sla',
    defaultPhase: 2,
    description: 'Define service level targets, operational metrics, and reporting requirements',
    coreFeature: true
  },
  {
    name: 'Self-Service Portal Requirements',
    category: 'portal',
    defaultPhase: 2,
    description: 'Define portal branding, user experience, catalog structure, and self-service capabilities',
    coreFeature: true
  },
  {
    name: 'Notification & Communication Requirements',
    category: 'automation',
    defaultPhase: 2,
    description: 'Define notification templates, triggers, and communication workflows',
    coreFeature: true
  },

  // ===== PHASE 3: DESIGN & CONFIGURATION =====
  {
    name: 'Organizational Data Setup',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Configure organizations, departments, locations, and business services in Ivanti',
    coreFeature: true
  },
  {
    name: 'User & Role Configuration',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Create roles, teams, security groups, and assign permissions in Ivanti',
    coreFeature: true
  },
  {
    name: 'Incident Management Configuration',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Configure incident object, categories, statuses, workflows, and business rules',
    coreFeature: true
  },
  {
    name: 'Service Catalog Design',
    category: 'request-type-group',
    defaultPhase: 3,
    description: 'Build service catalog structure, categories, and request offerings using Catalog Designer',
    coreFeature: true
  },
  {
    name: 'Service Request Forms',
    category: 'request-type-group',
    defaultPhase: 3,
    description: 'Design and build dynamic request forms with conditional logic and validation',
    coreFeature: true
  },
  {
    name: 'Change Management Configuration',
    category: 'approval',
    defaultPhase: 3,
    description: 'Configure change object, change types, approval workflows, and CAB functionality',
    coreFeature: true
  },
  {
    name: 'Problem Management Configuration',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Configure problem object, workflows, and link to incident/change records',
    coreFeature: true
  },
  {
    name: 'CMDB Configuration',
    category: 'asset',
    defaultPhase: 3,
    description: 'Configure CI classes, attributes, relationships, and dependency mapping',
    requiresAssets: true
  },
  {
    name: 'Discovery Integration',
    category: 'asset',
    defaultPhase: 3,
    description: 'Integrate Ivanti Discovery or third-party discovery tools for automated asset population',
    requiresAssets: true
  },
  {
    name: 'Knowledge Base Setup',
    category: 'knowledge-base',
    defaultPhase: 3,
    description: 'Configure knowledge base, article templates, categories, and approval workflow',
    coreFeature: true
  },
  {
    name: 'SLA Configuration',
    category: 'sla',
    defaultPhase: 3,
    description: 'Configure SLA definitions, targets, escalations, and calendar settings',
    coreFeature: true
  },
  {
    name: 'Business Rule Engine',
    category: 'automation',
    defaultPhase: 3,
    description: 'Configure business rules for auto-assignment, escalations, and field automation',
    coreFeature: true
  },
  {
    name: 'Notification Templates',
    category: 'automation',
    defaultPhase: 3,
    description: 'Design email and in-app notification templates with dynamic content',
    coreFeature: true
  },
  {
    name: 'Self-Service Portal Configuration',
    category: 'portal',
    defaultPhase: 3,
    description: 'Configure portal branding, themes, widgets, and user experience',
    coreFeature: true
  },
  {
    name: 'Quick Actions Configuration',
    category: 'automation',
    defaultPhase: 3,
    description: 'Configure quick actions for common tasks and bulk operations',
    coreFeature: false
  },

  // ===== PHASE 4: INTEGRATIONS & ADVANCED FEATURES =====
  {
    name: 'Active Directory Integration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Configure LDAP/AD integration for user authentication and synchronization',
    coreFeature: true
  },
  {
    name: 'Email Integration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure inbound email processing and outbound SMTP settings',
    coreFeature: true
  },
  {
    name: 'Monitoring Tool Integration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Integrate with monitoring tools (SCOM, SolarWinds, etc.) for automated incident creation',
    coreFeature: false
  },
  {
    name: 'ChatBot Configuration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure Ivanti Neurons AI chatbot for self-service and deflection',
    coreFeature: false
  },
  {
    name: 'Mobile App Configuration',
    category: 'portal',
    defaultPhase: 4,
    description: 'Configure Ivanti mobile apps for technicians and end users',
    coreFeature: false
  },
  {
    name: 'REST API Integration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Develop custom REST API integrations with third-party systems',
    coreFeature: false
  },
  {
    name: 'Process Automation Workflows',
    category: 'automation',
    defaultPhase: 4,
    description: 'Build advanced automation workflows using Ivanti Process Designer',
    coreFeature: false
  },
  {
    name: 'Custom Dashboard Creation',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Design role-based dashboards with widgets, charts, and KPI visualizations',
    coreFeature: true
  },
  {
    name: 'Reporting Configuration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Build operational reports, SLA reports, and executive dashboards',
    coreFeature: true
  },
  {
    name: 'Survey Configuration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure customer satisfaction surveys and feedback mechanisms',
    coreFeature: false
  },
  {
    name: 'Data Migration Execution',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Execute data migration from legacy systems with validation and reconciliation',
    coreFeature: false
  },

  // ===== PHASE 5: TESTING, TRAINING & DEPLOYMENT =====
  {
    name: 'Unit Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Test individual configurations, workflows, and business rules',
    coreFeature: true
  },
  {
    name: 'Integration Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Test end-to-end integrations with AD, email, monitoring tools, and APIs',
    coreFeature: true
  },
  {
    name: 'User Acceptance Testing (UAT)',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Conduct UAT with business stakeholders across all modules',
    coreFeature: true
  },
  {
    name: 'Performance Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Validate system performance under expected load conditions',
    coreFeature: false
  },
  {
    name: 'Training Material Development',
    category: 'knowledge-base',
    defaultPhase: 5,
    description: 'Create training guides, videos, and quick reference materials',
    coreFeature: true
  },
  {
    name: 'Administrator Training',
    category: 'knowledge-base',
    defaultPhase: 5,
    description: 'Conduct hands-on training for Ivanti administrators and power users',
    coreFeature: true
  },
  {
    name: 'End User Training',
    category: 'knowledge-base',
    defaultPhase: 5,
    description: 'Deliver training sessions for end users on self-service portal and ticket submission',
    coreFeature: true
  },
  {
    name: 'Production Deployment',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Deploy configurations to production environment with cutover plan',
    coreFeature: true
  },
  {
    name: 'Hypercare Support',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Provide intensive support during the first 2-4 weeks post-launch',
    coreFeature: true
  },
  {
    name: 'Knowledge Transfer',
    category: 'knowledge-base',
    defaultPhase: 5,
    description: 'Transfer technical and administrative knowledge to internal IT team',
    coreFeature: true
  },
  {
    name: 'Closeout Documentation',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Deliver as-built documentation, runbooks, and configuration guides',
    coreFeature: true
  }
];

// ===== BMC HELIX TEMPLATE =====
// BMC Helix ITSM implementation methodology
// Enterprise-grade ITSM platform with comprehensive 6-phase approach

export const BMC_HELIX_CONFIG_ITEMS: Omit<ConfigItem, 'id'>[] = [
  // ===== PHASE 1: INITIATION & FOUNDATION =====
  {
    name: 'Project Charter & Governance',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Define project scope, objectives, governance structure, and success criteria for BMC Helix implementation',
    coreFeature: true
  },
  {
    name: 'Environment Provisioning',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Set up BMC Helix environments (Development, Test, Production) and configure infrastructure',
    coreFeature: true
  },
  {
    name: 'Technical Architecture Design',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Design technical architecture including high availability, disaster recovery, and integration points',
    coreFeature: true
  },
  {
    name: 'Security & Access Control Planning',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Plan security model, role-based access control, and compliance requirements',
    coreFeature: true
  },
  {
    name: 'Integration Strategy',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Define integration architecture for AD, CMDB, monitoring tools, and enterprise systems',
    coreFeature: true
  },
  {
    name: 'Data Migration Planning',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Plan data migration approach, cleansing strategy, and validation procedures',
    coreFeature: false
  },

  // ===== PHASE 2: DISCOVERY & REQUIREMENTS =====
  {
    name: 'Current State Assessment',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Document current ITSM processes, pain points, and improvement opportunities',
    coreFeature: true
  },
  {
    name: 'Incident Management Requirements',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Define incident lifecycle, categorization, priority matrix, SLAs, and escalation procedures',
    coreFeature: true
  },
  {
    name: 'Service Request Management Requirements',
    category: 'request-type-group',
    defaultPhase: 2,
    description: 'Catalog service requests, define catalog structure, and fulfillment workflows',
    coreFeature: true
  },
  {
    name: 'Change Management Requirements',
    category: 'approval',
    defaultPhase: 2,
    description: 'Define change types (Standard, Normal, Emergency), CAB process, and risk assessment framework',
    coreFeature: true
  },
  {
    name: 'Problem Management Requirements',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Define problem lifecycle, root cause analysis methodology, and known error database',
    coreFeature: true
  },
  {
    name: 'Configuration Management Requirements',
    category: 'asset',
    defaultPhase: 2,
    description: 'Define CMDB strategy, CI classes, relationships, and federation approach',
    requiresAssets: true
  },
  {
    name: 'Knowledge Management Requirements',
    category: 'knowledge-base',
    defaultPhase: 2,
    description: 'Define knowledge base structure, article lifecycle, and governance',
    coreFeature: true
  },
  {
    name: 'Asset Management Requirements',
    category: 'asset',
    defaultPhase: 2,
    description: 'Define asset lifecycle management, financial tracking, and contract management needs',
    requiresAssets: true
  },
  {
    name: 'SLA & OLA Requirements',
    category: 'sla',
    defaultPhase: 2,
    description: 'Define service level agreements, operational level agreements, and measurement criteria',
    coreFeature: true
  },
  {
    name: 'Self-Service Portal Requirements',
    category: 'portal',
    defaultPhase: 2,
    description: 'Define digital workplace requirements, branding, and user experience',
    coreFeature: true
  },

  // ===== PHASE 3: DESIGN & PLANNING =====
  {
    name: 'Detailed Design Document',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Create comprehensive design document covering all ITSM modules and configurations',
    coreFeature: true
  },
  {
    name: 'CMDB Design & Federation',
    category: 'asset',
    defaultPhase: 3,
    description: 'Design CMDB schema, CI classes, relationships, and federation with discovery tools',
    requiresAssets: true
  },
  {
    name: 'Workflow Design',
    category: 'automation',
    defaultPhase: 3,
    description: 'Design workflows for incident, request, change, and problem management',
    coreFeature: true
  },
  {
    name: 'Integration Design',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Design integrations with AD, monitoring, discovery, and third-party systems',
    coreFeature: true
  },
  {
    name: 'Reporting & Analytics Design',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Design operational reports, dashboards, and KPI tracking',
    coreFeature: true
  },
  {
    name: 'Migration Design & Testing',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Design data migration procedures and conduct pilot migrations',
    coreFeature: false
  },

  // ===== PHASE 4: BUILD & CONFIGURATION =====
  {
    name: 'Foundation Configuration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Configure companies, organizations, people, support groups, and locations',
    coreFeature: true
  },
  {
    name: 'Role & Permission Configuration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Configure roles, permissions, security groups, and access controls',
    coreFeature: true
  },
  {
    name: 'Incident Management Configuration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Configure incident module, templates, categories, workflows, and business rules',
    coreFeature: true
  },
  {
    name: 'Service Request Catalog Build',
    category: 'request-type-group',
    defaultPhase: 4,
    description: 'Build service catalog, request definitions, and smart forms',
    coreFeature: true
  },
  {
    name: 'Request Fulfillment Configuration',
    category: 'request-type-group',
    defaultPhase: 4,
    description: 'Configure request workflows, approvals, and task automation',
    coreFeature: true
  },
  {
    name: 'Change Management Configuration',
    category: 'approval',
    defaultPhase: 4,
    description: 'Configure change module, workflows, CAB scheduler, and approval processes',
    coreFeature: true
  },
  {
    name: 'Problem Management Configuration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Configure problem module, investigation workflows, and known error database',
    coreFeature: true
  },
  {
    name: 'CMDB Configuration',
    category: 'asset',
    defaultPhase: 4,
    description: 'Configure CMDB, CI classes, attributes, relationships, and impact analysis',
    requiresAssets: true
  },
  {
    name: 'Discovery Integration',
    category: 'asset',
    defaultPhase: 4,
    description: 'Integrate BMC Discovery or third-party discovery tools for automated CMDB population',
    requiresAssets: true
  },
  {
    name: 'Asset Management Configuration',
    category: 'asset',
    defaultPhase: 4,
    description: 'Configure asset lifecycle, financial management, and contract tracking',
    requiresAssets: true
  },
  {
    name: 'Knowledge Management Configuration',
    category: 'knowledge-base',
    defaultPhase: 4,
    description: 'Configure knowledge base, article templates, workflows, and search capabilities',
    coreFeature: true
  },
  {
    name: 'SLA Configuration',
    category: 'sla',
    defaultPhase: 4,
    description: 'Configure SLA definitions, goals, milestones, and escalation rules',
    coreFeature: true
  },
  {
    name: 'Smart IT Configuration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure AI-powered features including Smart Recorder, Categorization, and Assignment',
    coreFeature: false
  },
  {
    name: 'Notification & Email Configuration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure notification templates, email handlers, and communication workflows',
    coreFeature: true
  },
  {
    name: 'Digital Workplace Portal',
    category: 'portal',
    defaultPhase: 4,
    description: 'Configure self-service portal, branding, widgets, and user experience',
    coreFeature: true
  },
  {
    name: 'Reporting & Dashboard Configuration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Build operational reports, executive dashboards, and KPI tracking',
    coreFeature: true
  },
  {
    name: 'Business Rules & Automation',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure filters, escalations, active links, and workflow automation',
    coreFeature: true
  },

  // ===== PHASE 5: INTEGRATION & ADVANCED FEATURES =====
  {
    name: 'Active Directory Integration',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Configure LDAP/AD integration for authentication and user synchronization',
    coreFeature: true
  },
  {
    name: 'Email Integration',
    category: 'automation',
    defaultPhase: 5,
    description: 'Configure email engine for inbound ticket creation and outbound notifications',
    coreFeature: true
  },
  {
    name: 'Monitoring Tool Integration',
    category: 'automation',
    defaultPhase: 5,
    description: 'Integrate with BMC TrueSight, SCOM, or third-party monitoring for automated incident creation',
    coreFeature: false
  },
  {
    name: 'CMDB Federation',
    category: 'asset',
    defaultPhase: 5,
    description: 'Configure CMDB federation with external CMDBs and data sources',
    requiresAssets: true
  },
  {
    name: 'REST API & Web Services',
    category: 'automation',
    defaultPhase: 5,
    description: 'Develop custom integrations using BMC Helix REST API',
    coreFeature: false
  },
  {
    name: 'ChatOps Integration',
    category: 'automation',
    defaultPhase: 5,
    description: 'Integrate with Slack, Microsoft Teams for ChatOps capabilities',
    coreFeature: false
  },
  {
    name: 'Multi-Tenancy Configuration',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Configure multi-tenant architecture for shared services or MSP deployments',
    coreFeature: false
  },
  {
    name: 'Advanced Analytics',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Configure predictive analytics, trend analysis, and business intelligence',
    coreFeature: false
  },
  {
    name: 'Data Migration Execution',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Execute full data migration with validation and reconciliation',
    coreFeature: false
  },

  // ===== PHASE 6: TESTING, TRAINING & DEPLOYMENT =====
  {
    name: 'System Integration Testing',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Conduct comprehensive testing of all modules and integrations',
    coreFeature: true
  },
  {
    name: 'User Acceptance Testing',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Facilitate UAT with business stakeholders across all ITSM processes',
    coreFeature: true
  },
  {
    name: 'Performance Testing',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Conduct load testing and performance optimization',
    coreFeature: false
  },
  {
    name: 'Security Testing',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Perform security assessment and penetration testing',
    coreFeature: false
  },
  {
    name: 'Training Material Development',
    category: 'knowledge-base',
    defaultPhase: 6,
    description: 'Develop training materials, user guides, and quick reference cards',
    coreFeature: true
  },
  {
    name: 'Administrator Training',
    category: 'knowledge-base',
    defaultPhase: 6,
    description: 'Deliver comprehensive training for platform administrators',
    coreFeature: true
  },
  {
    name: 'End User Training',
    category: 'knowledge-base',
    defaultPhase: 6,
    description: 'Conduct end user training on self-service portal and ITSM processes',
    coreFeature: true
  },
  {
    name: 'Go-Live Planning & Execution',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Execute production deployment with detailed cutover plan',
    coreFeature: true
  },
  {
    name: 'Hypercare Support',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Provide intensive post-go-live support for 2-4 weeks',
    coreFeature: true
  },
  {
    name: 'Knowledge Transfer & Handover',
    category: 'knowledge-base',
    defaultPhase: 6,
    description: 'Transfer knowledge to internal teams with documentation and runbooks',
    coreFeature: true
  },
  {
    name: 'Project Closeout',
    category: 'field-configuration',
    defaultPhase: 6,
    description: 'Deliver as-built documentation, lessons learned, and project closure',
    coreFeature: true
  }
];

// ===== FRESHSERVICE TEMPLATE =====
// Freshservice ITSM implementation methodology
// Modern cloud ITSM platform with 5-phase rapid deployment approach

export const FRESHSERVICE_CONFIG_ITEMS: Omit<ConfigItem, 'id'>[] = [
  // ===== PHASE 1: SETUP & PLANNING =====
  {
    name: 'Project Kickoff & Planning',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Define project scope, timeline, stakeholders, and success metrics for Freshservice implementation',
    coreFeature: true
  },
  {
    name: 'Instance Provisioning',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Provision Freshservice instance and configure basic settings',
    coreFeature: true
  },
  {
    name: 'Account Structure Setup',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Configure departments, locations, and organizational hierarchy',
    coreFeature: true
  },
  {
    name: 'Integration Planning',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Plan integrations with AD, SSO, email, and third-party tools',
    coreFeature: true
  },
  {
    name: 'Data Migration Strategy',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Plan approach for migrating tickets, assets, and knowledge from legacy systems',
    coreFeature: false
  },

  // ===== PHASE 2: CONFIGURATION & DESIGN =====
  {
    name: 'User & Agent Setup',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Import users, configure agent roles, and set up support groups',
    coreFeature: true
  },
  {
    name: 'Ticket Type Configuration',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Configure incident, service request, problem, and change ticket types',
    coreFeature: true
  },
  {
    name: 'Custom Fields & Forms',
    category: 'field-configuration',
    defaultPhase: 2,
    description: 'Design and configure custom fields, conditional forms, and ticket layouts',
    coreFeature: true
  },
  {
    name: 'Ticket Workflow Configuration',
    category: 'automation',
    defaultPhase: 2,
    description: 'Configure ticket statuses, transitions, and workflow rules',
    coreFeature: true
  },
  {
    name: 'Service Catalog Design',
    category: 'request-type-group',
    defaultPhase: 2,
    description: 'Build service catalog structure and request item templates',
    coreFeature: true
  },
  {
    name: 'Service Request Items',
    category: 'request-type-group',
    defaultPhase: 2,
    description: 'Create service request items with custom forms and fulfillment workflows',
    coreFeature: true
  },
  {
    name: 'Approval Workflows',
    category: 'approval',
    defaultPhase: 2,
    description: 'Configure multi-level approval workflows for requests and changes',
    coreFeature: true
  },
  {
    name: 'SLA Policy Configuration',
    category: 'sla',
    defaultPhase: 2,
    description: 'Define SLA policies, response times, resolution times, and business hours',
    coreFeature: true
  },
  {
    name: 'Email Notification Templates',
    category: 'automation',
    defaultPhase: 2,
    description: 'Customize email templates for ticket notifications and updates',
    coreFeature: true
  },
  {
    name: 'Knowledge Base Setup',
    category: 'knowledge-base',
    defaultPhase: 2,
    description: 'Configure knowledge base structure, categories, and article templates',
    coreFeature: true
  },

  // ===== PHASE 3: AUTOMATION & ADVANCED FEATURES =====
  {
    name: 'Automation Rules',
    category: 'automation',
    defaultPhase: 3,
    description: 'Configure workflow automators for auto-assignment, escalations, and notifications',
    coreFeature: true
  },
  {
    name: 'Observer Rules',
    category: 'automation',
    defaultPhase: 3,
    description: 'Set up time-based automations and SLA escalations',
    coreFeature: true
  },
  {
    name: 'Asset Management Configuration',
    category: 'asset',
    defaultPhase: 3,
    description: 'Configure asset types, relationships, and lifecycle management',
    requiresAssets: true
  },
  {
    name: 'Asset Discovery Setup',
    category: 'asset',
    defaultPhase: 3,
    description: 'Deploy and configure Freshservice Discovery Agent for automatic asset scanning',
    requiresAssets: true
  },
  {
    name: 'CMDB Configuration',
    category: 'asset',
    defaultPhase: 3,
    description: 'Configure CI types, relationships, and impact analysis',
    requiresAssets: true
  },
  {
    name: 'Change Management Configuration',
    category: 'approval',
    defaultPhase: 3,
    description: 'Configure change types, CAB workflows, and risk assessment',
    coreFeature: true
  },
  {
    name: 'Problem Management Configuration',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Set up problem management workflows and root cause analysis',
    coreFeature: true
  },
  {
    name: 'Portal Customization',
    category: 'portal',
    defaultPhase: 3,
    description: 'Customize self-service portal branding, layout, and widgets',
    coreFeature: true
  },
  {
    name: 'Canned Responses',
    category: 'knowledge-base',
    defaultPhase: 3,
    description: 'Create canned responses library for common scenarios',
    coreFeature: true
  },
  {
    name: 'Reports & Analytics',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Configure custom reports, dashboards, and scheduled reports',
    coreFeature: true
  },

  // ===== PHASE 4: INTEGRATIONS & AI FEATURES =====
  {
    name: 'Single Sign-On (SSO) Integration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Configure SAML 2.0 or OAuth SSO with identity provider',
    coreFeature: true
  },
  {
    name: 'Active Directory Integration',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Set up AD sync for user and agent provisioning',
    coreFeature: true
  },
  {
    name: 'Email Integration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure support email addresses and email-to-ticket conversion',
    coreFeature: true
  },
  {
    name: 'Freddy AI Configuration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Enable and train Freddy AI for intelligent ticket assignment and suggestions',
    coreFeature: false
  },
  {
    name: 'Chatbot Configuration',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure Freddy Chatbot for automated support and ticket deflection',
    coreFeature: false
  },
  {
    name: 'Marketplace App Integrations',
    category: 'automation',
    defaultPhase: 4,
    description: 'Install and configure marketplace apps (Slack, Teams, Zoom, Jira, etc.)',
    coreFeature: false
  },
  {
    name: 'Webhooks & API Integrations',
    category: 'automation',
    defaultPhase: 4,
    description: 'Configure webhooks and REST API integrations with external systems',
    coreFeature: false
  },
  {
    name: 'Mobile App Configuration',
    category: 'portal',
    defaultPhase: 4,
    description: 'Configure and deploy Freshservice mobile apps for agents',
    coreFeature: false
  },
  {
    name: 'Data Migration Execution',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Import historical data from legacy systems with validation',
    coreFeature: false
  },

  // ===== PHASE 5: TESTING, TRAINING & GO-LIVE =====
  {
    name: 'System Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Test all configurations, workflows, and integrations',
    coreFeature: true
  },
  {
    name: 'User Acceptance Testing',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Conduct UAT with end users and stakeholders',
    coreFeature: true
  },
  {
    name: 'Training Material Creation',
    category: 'knowledge-base',
    defaultPhase: 5,
    description: 'Create training videos, guides, and knowledge articles',
    coreFeature: true
  },
  {
    name: 'Agent Training',
    category: 'knowledge-base',
    defaultPhase: 5,
    description: 'Train support agents on Freshservice platform and workflows',
    coreFeature: true
  },
  {
    name: 'End User Training',
    category: 'knowledge-base',
    defaultPhase: 5,
    description: 'Train end users on self-service portal and ticket submission',
    coreFeature: true
  },
  {
    name: 'Go-Live Preparation',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Finalize configurations, communications, and cutover plan',
    coreFeature: true
  },
  {
    name: 'Production Launch',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Execute go-live with phased or full rollout',
    coreFeature: true
  },
  {
    name: 'Post-Launch Support',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Provide hypercare support for first 2 weeks after launch',
    coreFeature: true
  },
  {
    name: 'Optimization & Tuning',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Fine-tune configurations based on production usage patterns',
    coreFeature: true
  },
  {
    name: 'Documentation & Handover',
    category: 'knowledge-base',
    defaultPhase: 5,
    description: 'Deliver configuration documentation and knowledge transfer',
    coreFeature: true
  }
];

// ===== CUSTOM TEMPLATE =====
// Custom ITSM template for non-platform-specific implementations

export const CUSTOM_CONFIG_ITEMS: Omit<ConfigItem, 'id'>[] = [
  {
    name: 'Requirements Gathering',
    category: 'field-configuration',
    defaultPhase: 1,
    description: 'Document business requirements and success criteria',
    coreFeature: true
  },
  {
    name: 'Process Design',
    category: 'automation',
    defaultPhase: 2,
    description: 'Design ITSM processes and workflows',
    coreFeature: true
  },
  {
    name: 'Configuration',
    category: 'field-configuration',
    defaultPhase: 3,
    description: 'Configure ITSM platform according to design specifications',
    coreFeature: true
  },
  {
    name: 'Testing & Validation',
    category: 'field-configuration',
    defaultPhase: 4,
    description: 'Test configurations and validate against requirements',
    coreFeature: true
  },
  {
    name: 'Training & Go-Live',
    category: 'field-configuration',
    defaultPhase: 5,
    description: 'Conduct training and launch the ITSM solution',
    coreFeature: true
  }
];

/**
 * Get default config items for a specific template type
 */
export function getTemplateConfigItems(templateType: string): Omit<ConfigItem, 'id'>[] {
  const templateMap: Record<string, Omit<ConfigItem, 'id'>[]> = {
    'atlassian': ATLASSIAN_CONFIG_ITEMS,
    'servicenow': SERVICENOW_CONFIG_ITEMS,
    'ivanti': IVANTI_CONFIG_ITEMS,
    'bmc-helix': BMC_HELIX_CONFIG_ITEMS,
    'freshservice': FRESHSERVICE_CONFIG_ITEMS,
    'custom': CUSTOM_CONFIG_ITEMS
  };

  return templateMap[templateType] || CUSTOM_CONFIG_ITEMS;
}

/**
 * Generate template-specific config items with unique IDs
 */
export function generateTemplateConfigItems(templateType: string): ConfigItem[] {
  const items = getTemplateConfigItems(templateType);
  return items.map((item, index) => ({
    ...item,
    id: `${templateType}-config-${index + 1}-${Date.now()}`
  }));
}

/**
 * Get phase labels for a specific template
 */
export function getTemplatePhaseLabels(templateType: string): Record<number, string> {
  const atlassianPhases = {
    1: 'Phase 1 (Initiation)',
    2: 'Phase 2 (Discovery - ITIL 4 Workshops)',
    3: 'Phase 3 (Planning)',
    4: 'Phase 4 (Implementation)',
    5: 'Phase 5 (Testing)',
    6: 'Phase 6 (Launch & Support)'
  };

  const serviceNowPhases = {
    1: 'Phase 1 (Initiation & Foundation)',
    2: 'Phase 2 (Discovery & Requirements)',
    3: 'Phase 3 (Design & Planning)',
    4: 'Phase 4 (Build & Configuration)',
    5: 'Phase 5 (Testing & Validation)',
    6: 'Phase 6 (Deployment & Support)'
  };

  const ivantiPhases = {
    1: 'Phase 1 (Foundation & Planning)',
    2: 'Phase 2 (Discovery & Requirements)',
    3: 'Phase 3 (Design & Configuration)',
    4: 'Phase 4 (Integrations & Advanced Features)',
    5: 'Phase 5 (Testing, Training & Deployment)'
  };

  const bmcHelixPhases = {
    1: 'Phase 1 (Initiation & Foundation)',
    2: 'Phase 2 (Discovery & Requirements)',
    3: 'Phase 3 (Design & Planning)',
    4: 'Phase 4 (Build & Configuration)',
    5: 'Phase 5 (Integration & Advanced Features)',
    6: 'Phase 6 (Testing, Training & Deployment)'
  };

  const freshservicePhases = {
    1: 'Phase 1 (Setup & Planning)',
    2: 'Phase 2 (Configuration & Design)',
    3: 'Phase 3 (Automation & Advanced Features)',
    4: 'Phase 4 (Integrations & AI Features)',
    5: 'Phase 5 (Testing, Training & Go-Live)'
  };

  const customPhases = {
    1: 'Phase 1 (Core)',
    2: 'Phase 2 (Automation)',
    3: 'Phase 3 (Portal)',
    4: 'Phase 4 (Advanced)',
    5: 'Future Consideration'
  };

  const phaseMap: Record<string, Record<number, string>> = {
    'atlassian': atlassianPhases,
    'servicenow': serviceNowPhases,
    'ivanti': ivantiPhases,
    'bmc-helix': bmcHelixPhases,
    'freshservice': freshservicePhases,
    'custom': customPhases
  };

  return phaseMap[templateType] || customPhases;
}

/**
 * Get a description of the template's methodology
 */
export function getTemplateMethodologyDescription(templateType: string): string {
  const descriptions: Record<string, string> = {
    'atlassian': 'Atlassian ITSM implementation following a 6-phase methodology aligned with ITIL 4 best practices: Initiation, Discovery (ITIL 4 Workshops), Planning, Implementation, Testing, and Launch/Support.',
    'servicenow': 'ServiceNow ITSM implementation following a comprehensive 6-phase methodology: Initiation & Foundation, Discovery & Requirements (ITIL 4 workshops), Design & Planning, Build & Configuration, Testing & Validation, and Deployment & Support. Covers core ITSM modules, CMDB, Service Catalog, integrations, and advanced features.',
    'ivanti': 'Ivanti Neurons for ITSM implementation following a 5-phase methodology: Foundation & Planning, Discovery & Requirements, Design & Configuration, Integrations & Advanced Features, and Testing/Training/Deployment. Covers incident, request, change, problem management, CMDB, self-service portal, automation, and integrations.',
    'bmc-helix': 'BMC Helix ITSM enterprise implementation following a comprehensive 6-phase methodology: Initiation & Foundation, Discovery & Requirements, Design & Planning, Build & Configuration, Integration & Advanced Features, and Testing/Training/Deployment. Covers all ITSM modules, enterprise CMDB, Smart IT (AI), multi-tenancy, advanced integrations, and analytics.',
    'freshservice': 'Freshservice cloud ITSM implementation following a streamlined 5-phase rapid deployment methodology: Setup & Planning, Configuration & Design, Automation & Advanced Features, Integrations & AI Features (Freddy AI), and Testing/Training/Go-Live. Modern approach covering ITSM, asset management, self-service, and intelligent automation.',
    'custom': 'Custom ITSM implementation methodology suitable for any platform or custom solution.'
  };

  return descriptions[templateType] || descriptions.custom;
}
